import { call, put, takeLatest } from "redux-saga/effects";
import * as actions from "./actions";
import * as actionTypes from "./actionTypes";
import { db } from "helpers/firebase/firebaseConfig";
import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  updateDoc, 
  addDoc, 
  deleteDoc,
  query,
  where
} from "firebase/firestore";
import { v4 as uuidv4 } from 'uuid';
import s3, { spacesEndpoint } from "helpers/digital/DigitalOcean";
import digitalPoints from "helpers/digital/digitalPoints"

// Firebase Collection Names
const GENERAL_SETTINGS = "platform_settings";
const SUPER_ADMIN = "db_super_admins";
const APP_NOTIFICATIONS = "app_notifications";
const SERVICE_PROVIDER_NOTIFICATIONS = "serviceProvidersNotifications";

async function fetchSetting() {
  try {
    const settingsRef = collection(db, GENERAL_SETTINGS);
    const querySnapshot = await getDocs(settingsRef);
    if (!querySnapshot.empty) {

      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    }
   
    return null;
  } catch (error) {
    throw error;
  }
}

async function fetchAdmins() {
  try {
    const querySnapshot = await getDocs(collection(db, SUPER_ADMIN));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    throw error;
  }
}

function* fetchAdminsSaga() {
  try {
    const admins = yield call(fetchAdmins);
    yield put(actions.fetchAdminsSuccess(admins));
  } catch (error) {
    yield put(actions.fetchAdminsFailure(error));
    throw error;
  }
}

const changeAdminRoleApi = async (adminId, newRole) => {
  try {
    const adminRef = doc(db, SUPER_ADMIN, adminId);
    await updateDoc(adminRef, { role: newRole });
    return adminId;
  } catch (error) {
    throw error;
  }
};

function* changeAdminRoleSaga(action) {
  try {
    const { adminId, newRole } = action.payload;
    yield call(changeAdminRoleApi, adminId, newRole);
    const admins = yield call(fetchAdmins); 
    yield put(actions.fetchAdminsSuccess(admins));
  } catch (error) {
    throw error;
  }
}

// Batch processing function for notifications
const batchCreateDocuments = async (collectionName, documents) => {
  const results = [];
  const batchSize = 1; // Define a batch size that suits your application

  for (let i = 0; i < documents.length; i += batchSize) {
    const batch = documents.slice(i, i + batchSize);
    const batchResults = await Promise.all(
      batch.map(docData => addDoc(collection(db, collectionName), docData))
    );
    results.push(...batchResults);
  }

  return results;
};

export const sendNotificationAppUsers = async (notificationData, targetIds) => {
  try {
    const documents = targetIds.map(id => ({
      title: notificationData.title,
      content: notificationData.content,
      created_at: new Date().toISOString(),
      notification_type: notificationData.notification_type,
      isSeen: false,
      appUser: id
    }));

    return await batchCreateDocuments(APP_NOTIFICATIONS, documents);
  } catch (error) {
    throw error;
  }
};

export const sendNotificationServiceProvider = async (notificationData, targetIds) => {
  try {
    const documents = targetIds.map(id => ({
      title: notificationData.title,
      content: notificationData.content,
      created_at: new Date().toISOString(),
      notification_type: notificationData.notification_type,
      seen: false,
      serviceProviders: id
    }));
    return await batchCreateDocuments(SERVICE_PROVIDER_NOTIFICATIONS, documents);
  } catch (error) {
    throw error;
  }
};

function* sendNotificationSaga(action) {
  try {
    const { notificationData, targetIds, notificationTarget } = action.payload;

    if (notificationTarget === 'serviceProviders') {
      yield call(sendNotificationServiceProvider, notificationData, targetIds);
    } else if (notificationTarget === 'appUsers') {
      yield call(sendNotificationAppUsers, notificationData, targetIds);
    }
  } catch (error) {
    throw error;
  }
}

// Upload banner via server endpoint to avoid CORS
async function uploadBannerApi(formData) {
  try {
    const img = formData;
    
    // Convert file to base64
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.onload = async () => {
        try {
          const base64 = reader.result.split(',')[1]; // Remove data:image/... prefix
          
          // Get backend URL

          const url =  'https://node-server-eta-coral.vercel.app/upload-banner';
          
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': process.env.REACT_APP_NODE_API_KEY // Send API key if available
            },
            body: JSON.stringify({
              fileName: img.name,
              fileBase64: base64,
              contentType: img.type
            })
          });

          if (!response.ok) {
            const text = await response.text().catch(() => '');
            reject(new Error(`Upload failed: ${response.status} ${text}`));
            return;
          }

          const json = await response.json();
          resolve(json);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsDataURL(img);
    });
  } catch (error) {
    throw error;
  }
}

function* uploadBannerSaga(action) {
  try {
    const formData = action.payload;
    yield call(uploadBannerApi, formData);
    yield put(actions.fetchDataRequest());
  } catch (error) {
    console.log('Failed to upload banner:', error);
  }
}

const fetchBannerLinks = async () => {
  try {
    // Prefer explicit backend URL from env, fall back to same-origin /list-banners


    const url =  'https://node-server-eta-coral.vercel.app/list-banners';

    const resp = await fetch(url);
    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      console.error(`Failed to fetch banners: ${resp.status} ${text}`);
      return [];
    }
    const json = await resp.json();
    return json.images || [];
  } catch (error) {
    console.error("Error listing images via server:", error);
    return [];
  }
};

function* fetchDataSaga() {
  try {
    const response = yield call(fetchBannerLinks); 
    yield put(actions.fetchDataSuccess(response));
  } catch (error) {
    console.error("Error in fetchDataSaga:", error);
    yield put(actions.fetchDataFailure(error));
    // do not rethrow - prevent root saga from crashing
  }
}

const deleteDataApi = async (data) => {
  try {
    const { keyName } = data;
    
    if (!keyName) {
      throw new Error('Missing keyName for deletion');
    }

    console.log('Sending delete request for:', keyName);

    // Get API key from env or from localStorage
    const apiKey = process.env.REACT_APP_NODE_API_KEY 

    // Call server endpoint to delete banner (using main server)
    const response = await fetch('https://node-server-eta-coral.vercel.app/delete-banner', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey
      },
      body: JSON.stringify({ keyName })
    });

    // console.log('Delete response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Delete error response:', errorData);
      
      // Check for time sync error
      if (errorData.code === 'TIME_SYNC_ERROR') {
        throw new Error('⏰ System clock out of sync: ' + errorData.details + ' Please sync your system time.');
      }
      
      throw new Error(errorData.error || `Failed to delete banner: ${response.status}`);
    }

    const result = await response.json();
    // console.log('Banner deleted successfully:', result);
    return result;
  } catch (error) {
    console.error("Error deleting banner:", error);
    throw error;
  }
};

function* deleteDataSaga(action) {
  try {
    yield call(deleteDataApi, action.payload); 
    yield put(actions.fetchDataRequest());
  } catch (error) {
    throw error;
  }
}

async function UpdateCoRate(data) {
  const { id, newRate } = data;
  const settingsRef = doc(db, GENERAL_SETTINGS, id);
  await updateDoc(settingsRef, { commission_rates: newRate });
}

async function UpdateAppCoRate(data) {
  const { id, newRate } = data;
  const settingsRef = doc(db, GENERAL_SETTINGS, id);
  await updateDoc(settingsRef, { app_commission_rates: newRate });
}

async function UpdateTamaraCoRate(data) {
  const { id, newRate } = data;
  const settingsRef = doc(db, GENERAL_SETTINGS, id);
  await updateDoc(settingsRef, { tamara_commission: newRate });
}

async function aboutUs(data) {
  const { id, editedText } = data;
  const settingsRef = doc(db, GENERAL_SETTINGS, id);
  await updateDoc(settingsRef, { about_us: editedText });
}

async function changeMoode(data) {
  const { id, newMode } = data;
  const settingsRef = doc(db, GENERAL_SETTINGS, id);
  await updateDoc(settingsRef, { maintenance_mode: newMode });
}

function* fetchSettings() {
  try {
    const settings = yield call(fetchSetting);
    yield put(actions.fetchSettingsSuccess(settings));
  } catch (error) {
    yield put(actions.fetchSettingsFailure(error));
  }
}

async function updateSettingsApi(updatedSettings) {
  try {
    const { id, ...fields } = updatedSettings;
    if (!id) throw new Error('Missing settings id');
    const settingsRef = doc(db, GENERAL_SETTINGS, id);
    await updateDoc(settingsRef, fields);
  } catch (error) {
    throw error;
  }
}

function* updateSettingsSaga(action) {
  try {
    yield call(updateSettingsApi, action.payload);
    yield put(actions.updateSettingsSuccess());
    yield put(actions.fetchSettingsRequest());
  } catch (error) {
    yield put(actions.updateSettingsFailure(error));
    throw error;
  }
}

function* updateCommision(action) {
  try {
    yield call(UpdateCoRate, action.payload);
    yield put(actions.fetchSettingsRequest());
  } catch (error) {
    throw error;
  }
}

function* updateAppCommision(action) {
  try {
    yield call(UpdateAppCoRate, action.payload);
    yield put(actions.fetchSettingsRequest());
  } catch (error) {
    throw error;
  }
}

function* updateTamaraCommision(action) {
  try {
    yield call(UpdateTamaraCoRate, action.payload);
    yield put(actions.fetchSettingsRequest());
  } catch (error) {
    throw error;
  }
}

function* updateAboutUs(action) {
  try {
    yield call(aboutUs, action.payload);
    yield put(actions.fetchSettingsRequest());
  } catch (error) {
    throw error;
  }
}

function* maintenanceMood(action) {
  try {
    yield call(changeMoode, action.payload);
    yield put(actions.fetchSettingsRequest());
  } catch (error) {
    throw error;
  }
}

async function createSuperAdminApi(data) {
  try {
    const docRef = await addDoc(collection(db, SUPER_ADMIN), data);
    return { id: docRef.id, ...data };
  } catch (error) {
    throw error;
  }
}

function* createSuperAdminSaga(action) {
  try {
    yield call(createSuperAdminApi, action.payload);
    yield put(actions.fetchAdminsRequest());
  } catch (error) {
    throw error;
  }
}

async function deleteAdminApi(adminId) {  
  try {
    await deleteDoc(doc(db, SUPER_ADMIN, adminId));
    return adminId;
  } catch (error) {
    throw error;
  }
}

function* deleteAdminSaga(action) {
  try {
    yield call(deleteAdminApi, action.payload);
    yield put(actions.fetchAdminsRequest());
  } catch (error) {
    throw error;
  }
}

function* settingsSaga() {
  yield takeLatest(actionTypes.CHANGE_ADMIN_ROLE_REQUEST, changeAdminRoleSaga);
  yield takeLatest(actionTypes.FETCH_SETTINGS_REQUEST, fetchSettings);
  yield takeLatest(actionTypes.UPDATE_COMMISSION_RATE, updateCommision);
  yield takeLatest(actionTypes.UPDATE_APP_COMMISSION_RATE, updateAppCommision);
  yield takeLatest(actionTypes.UPDATE_TAMARA_COMMISSION_RATE, updateTamaraCommision);
  yield takeLatest(actionTypes.UPDATE_ABOUT_US, updateAboutUs);
  yield takeLatest(actionTypes.CHANGE_MAINANCE_MOODE, maintenanceMood);
  yield takeLatest(actionTypes.FETCH_ADMINS_REQUEST, fetchAdminsSaga);
  yield takeLatest(actionTypes.SEND_NOTIFICATION_REQUEST, sendNotificationSaga);
  yield takeLatest(actionTypes.UPLOAD_BANNER_REQUEST, uploadBannerSaga);
  yield takeLatest(actionTypes.FETCH_DATA_REQUEST, fetchDataSaga);
  yield takeLatest(actionTypes.DELETE_DATA_REQUEST, deleteDataSaga);
  yield takeLatest(actionTypes.CREATE_SUPERADMIN_REQUEST, createSuperAdminSaga);
  yield takeLatest(actionTypes.DELETE_ADMIN_REQUEST, deleteAdminSaga);
  yield takeLatest(actionTypes.UPDATE_SETTINGS_REQUEST, updateSettingsSaga);
}

export default settingsSaga;